import { useNavigate, useParams } from 'react-router-dom';
import { useAppContext } from '../../Context';
import { usePermissions } from '../../services';
import { useEffect, useState } from 'react';
import { CustomerSiigoType } from '../../types';
import { Grid, Card, CardContent, Typography, Box, Chip, Divider } from '@mui/material';
import { Button } from '../../components/shared/FormElements';

const ViewCustomer: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const { apiCall } = useAppContext();

  const { canView_customer } = usePermissions(['view_customer']);
  const [customer, setCustomer] = useState<CustomerSiigoType | null>(null);

  const getCustomer = async (id: string) => {
    const response = await apiCall('GET', `customers/${id}/`, null, '', 'No se pudo obtener el cliente', true);
    if (response) {
      setCustomer(response);
    }
  };

  useEffect(() => {
    if (!canView_customer) {
      navigate('/');
      return;
    }
    // check id param
    if (id) {
      getCustomer(id);
    } else {
      navigate('/clientes');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  if (!customer) {
    return (
      <div className='container-wrap'>
        <Typography variant='h6'>Cargando información del cliente...</Typography>
      </div>
    );
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className='container-wrap'>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={10} className='text-left'>
          <h2 className='text-left'>{Array.isArray(customer.name) ? customer.name.join(' ') : customer.name}</h2>
          {customer.commercial_name && (
            <Typography variant='subtitle1' color='textSecondary'>
              Nombre comercial: {customer.commercial_name}
            </Typography>
          )}
        </Grid>
        <Grid item xs={12} sm={2} className='text-right'>
          <Button className='btn btn-primary' onClick={() => navigate('/clientes')}>
            Volver a la lista de clientes
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={3}>
        {/* Información básica */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Información Básica
              </Typography>
              <Box sx={{ mb: 1 }}>
                <strong>ID:</strong> {customer.id}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Tipo:</strong> {customer.type}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Tipo de persona:</strong> {customer.person_type}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Tipo de identificación:</strong> {customer.id_type.name} ({customer.id_type.code})
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Identificación:</strong> {customer.identification}
              </Box>
              {customer.check_digit && (
                <Box sx={{ mb: 1 }}>
                  <strong>Dígito de verificación:</strong> {customer.check_digit}
                </Box>
              )}
              <Box sx={{ mb: 1 }}>
                <strong>Sucursal:</strong> {customer.branch_office}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Estado:</strong>{' '}
                <Chip
                  label={customer.active ? 'Activo' : 'Inactivo'}
                  color={customer.active ? 'success' : 'error'}
                  size='small'
                />
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Responsable de IVA:</strong>{' '}
                <Chip
                  label={customer.vat_responsible ? 'Sí' : 'No'}
                  color={customer.vat_responsible ? 'success' : 'default'}
                  size='small'
                />
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Creado:</strong> {formatDate(customer.metadata.created)}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Sincronizado:</strong>{' '}
                <Chip
                  label={customer.synced ? 'Sí' : 'No'}
                  color={customer.synced ? 'success' : 'default'}
                  size='small'
                />
              </Box>
              {customer.last_synced_at && (
                <Box sx={{ mb: 1 }}>
                  <strong>Última sincronización:</strong> {formatDate(customer.last_synced_at)}
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Dirección */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant='h6' gutterBottom>
                Dirección
              </Typography>
              <Box sx={{ mb: 1 }}>
                <strong>Dirección:</strong> {customer.address.address}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Ciudad:</strong> {customer.address.city.city_name}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Departamento/Estado:</strong> {customer.address.city.state_name}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>País:</strong> {customer.address.city.country_name}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Código ciudad:</strong> {customer.address.city.city_code}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Código departamento:</strong> {customer.address.city.state_code}
              </Box>
              <Box sx={{ mb: 1 }}>
                <strong>Código país:</strong> {customer.address.city.country_code}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Responsabilidades fiscales */}
        {customer.fiscal_responsibilities && customer.fiscal_responsibilities.length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Responsabilidades Fiscales
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                  {customer.fiscal_responsibilities.map((responsibility, index) => (
                    <Chip key={index} label={`${responsibility.name} (${responsibility.code})`} variant='outlined' />
                  ))}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Teléfonos */}
        {customer.phones && customer.phones.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Teléfonos
                </Typography>
                {customer.phones.map((phone, index) => (
                  <Box key={index} sx={{ mb: 1 }}>
                    <strong>Teléfono {index + 1}:</strong> {phone.indicative && `+${phone.indicative} `}
                    {phone.number}
                    {phone.extension && ` ext. ${phone.extension}`}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Contactos */}
        {customer.contacts && customer.contacts.length > 0 && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography variant='h6' gutterBottom>
                  Contactos
                </Typography>
                {customer.contacts.map((contact, index) => (
                  <Box key={index} sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Box sx={{ mb: 1 }}>
                      <strong>Nombre:</strong> {contact.first_name} {contact.last_name}
                    </Box>
                    {contact.email && (
                      <Box sx={{ mb: 1 }}>
                        <strong>Email:</strong> {contact.email}
                      </Box>
                    )}
                    {contact.phone && (
                      <Box sx={{ mb: 1 }}>
                        <strong>Teléfono:</strong> {contact.phone.indicative && `+${contact.phone.indicative} `}
                        {contact.phone.number}
                      </Box>
                    )}
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default ViewCustomer;
